import { useState } from 'react';
import { Button, Input, Modal, useToast } from '@bbpos/ui';
import { useCloseRemaining, useDecideAmendment } from '@/features/purchase-orders';
import type { PoRevision, PoRevisionChange, PurchaseOrder } from '@/features/purchase-orders';
import { GLASS } from '@/constants/design-tokens';
import { formatDocAmount } from '@/utils/purchase-currency';

// POS comments item 10 — the PO detail page's amendment pieces: the pending
// approval banner, the revision history and the close-remaining dialog.

const KIND_LABEL: Record<PoRevision['kind'], string> = {
  AMENDMENT: 'Amended',
  SUBSTITUTE: 'Substitute received',
  CLOSE_SHORT: 'Closed short',
};

const FIELD_LABEL: Record<string, string> = {
  quantity: 'qty', unit: 'unit', unit_cost: 'cost', gst_rate: 'tax %', discount_type: 'discount type', discount_value: 'discount',
};

/** One readable line per change, e.g. "LED GW (16W): qty 5 → 3, cost 2.000 → 2.100". */
export function describeChange(c: PoRevisionChange, money: (minor: number) => string): string {
  if (c.type === 'ADDED') return `Added ${c.name}${c.quantity != null ? ` × ${c.quantity}` : ''}`;
  if (c.type === 'REMOVED') return `Removed ${c.name}`;
  if (c.type === 'CLOSED') return `Closed ${c.name} — ${c.quantity} not coming`;
  if (c.type === 'SUBSTITUTE') return `${c.name} × ${c.quantity} received in place of ${c.replaces}`;
  const parts = Object.entries(c.fields ?? {}).map(([f, { from, to }]) => {
    const show = (v: unknown) => (f === 'unit_cost' ? money(Number(v)) : String(v));
    return `${FIELD_LABEL[f] ?? f} ${show(from)} → ${show(to)}`;
  });
  return `${c.name}: ${parts.join(', ')}`;
}

function when(iso: string) {
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function PendingAmendmentBanner({ po, isOwner, currency }: { po: PurchaseOrder; isOwner: boolean; currency: string }) {
  const { toast } = useToast();
  const decide = useDecideAmendment();
  const [note, setNote] = useState('');
  const pending = po.revisions?.find((r) => r.id === po.pending_amendment_id);
  if (!pending) return null;
  const money = (m: number) => formatDocAmount(m, currency);
  const proposedChanges = pending.changes.map((c) => describeChange(c, money));

  async function act(approve: boolean) {
    try {
      await decide.mutateAsync({ id: po.id, revisionId: pending!.id, approve, note: note.trim() || undefined });
      toast({ title: approve ? `Rev ${pending!.revision_no} approved and applied` : `Rev ${pending!.revision_no} rejected`, variant: 'success' });
    } catch (err) {
      toast({ title: (err as Error).message, variant: 'error' });
    }
  }

  return (
    <div role="alert" className="rounded-xl border border-warning-200 bg-gradient-to-r from-warning-50 to-amber-50 p-4 shadow-md animate-fade-in dark:border-warning-800 dark:from-warning-900/20 dark:to-amber-900/20">
      <p className="text-sm font-semibold text-warning-900 dark:text-warning-100">
        Amendment Rev {pending.revision_no} is waiting for owner approval
      </p>
      <p className="text-xs text-warning-800 dark:text-warning-200">
        By {pending.amended_by_name ?? 'a manager'} · {when(pending.created_at)} · total {money(pending.total_before)} → {money(pending.total_after)}
        {pending.reason ? ` · ${pending.reason}` : ''}
      </p>
      {proposedChanges.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-xs text-warning-900 dark:text-warning-100">
          {proposedChanges.map((t) => <li key={t}>{t}</li>)}
        </ul>
      )}
      {isOwner && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="w-64"><Input value={note} onChange={setNote} placeholder="Note (optional)" /></div>
          <Button variant="primary" size="sm" onClick={() => { void act(true); }} disabled={decide.isPending}>Approve</Button>
          <Button variant="secondary" size="sm" onClick={() => { void act(false); }} disabled={decide.isPending}>Reject</Button>
        </div>
      )}
    </div>
  );
}

export function PoRevisionHistory({ po, currency }: { po: PurchaseOrder; currency: string }) {
  const revisions = po.revisions ?? [];
  if (revisions.length === 0) return null;
  const money = (m: number) => formatDocAmount(m, currency);
  return (
    <div className={`${GLASS} p-6`}>
      <h3 className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-200">Revisions ({revisions.length})</h3>
      <ul className="space-y-3">
        {revisions.map((r) => (
          <li key={r.id} className="animate-slide-up rounded-lg border border-neutral-100 p-3 transition-all duration-200 hover:shadow-md dark:border-neutral-800">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-mono font-semibold text-neutral-900 dark:text-white">Rev {r.revision_no}</span>
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">{KIND_LABEL[r.kind]}</span>
              {r.status !== 'APPLIED' && (
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${r.status === 'PENDING_APPROVAL' ? 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300' : 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300'}`}>
                  {r.status === 'PENDING_APPROVAL' ? 'Waiting for approval' : 'Rejected'}
                </span>
              )}
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                {r.amended_by_name ?? '—'} · {when(r.created_at)}
                {r.total_after !== r.total_before ? ` · total ${money(r.total_before)} → ${money(r.total_after)}` : ''}
              </span>
            </div>
            {r.reason && <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-300">{r.reason}</p>}
            <ul className="mt-1 space-y-0.5 text-xs tabular-nums text-neutral-700 dark:text-neutral-300">
              {r.changes.map((c, i) => <li key={i}>{describeChange(c, money)}</li>)}
            </ul>
            {r.decided_by_name && (
              <p className="mt-1 text-[11px] text-neutral-500">
                {r.status === 'REJECTED' ? 'Rejected' : 'Approved'} by {r.decided_by_name}{r.decision_note ? ` — ${r.decision_note}` : ''}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CloseRemainingModal({
  po, itemIds, onClose,
}: { po: PurchaseOrder; itemIds?: string[]; onClose: () => void }) {
  const { toast } = useToast();
  const close = useCloseRemaining();
  const [reason, setReason] = useState('');
  const targets = po.items.filter((i) => i.status !== 'CLOSED' && i.received_quantity < i.quantity && (!itemIds || itemIds.includes(i.id)));

  async function submit() {
    try {
      const res = await close.mutateAsync({ id: po.id, item_ids: itemIds, reason: reason.trim() });
      toast({ title: res.status === 'RECEIVED' ? `${po.po_number} is now complete` : 'Remaining quantity closed', variant: 'success' });
      onClose();
    } catch (err) {
      toast({ title: (err as Error).message, variant: 'error' });
    }
  }

  return (
    <Modal open title="Close remaining quantity" onClose={onClose}>
      <div className="space-y-4 p-4">
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          The supplier won&apos;t send the rest of {targets.length === 1 ? 'this line' : 'these lines'}. What has arrived stays as received.
        </p>
        <ul className="space-y-1 text-sm">
          {targets.map((i) => (
            <li key={i.id} className="flex justify-between gap-3">
              <span className="font-medium text-neutral-800 dark:text-neutral-100">{i.product_name}</span>
              <span className="tabular-nums text-neutral-500">{i.quantity - i.received_quantity} {i.unit} not coming</span>
            </li>
          ))}
        </ul>
        <div>
          <label htmlFor="close-reason" className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-200">Reason</label>
          <Input id="close-reason" value={reason} onChange={setReason} placeholder="e.g. Discontinued by the supplier" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => { void submit(); }} disabled={!reason.trim() || targets.length === 0 || close.isPending}>
            {close.isPending ? 'Closing…' : 'Close remaining'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
